import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, LogOut, Monitor, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { maskE164 } from "@/lib/phone";
import { Button, Card, Input, Overline, Toggle } from "@/components/app/Primitives";
import { PhoneInput } from "@/components/app/PhoneInput";
import { StackPage } from "@/components/app/Layout";
import { Spinner } from "@/components/app/Spinner";

export default function Security() {
  const { user, refreshMe } = useAuth();
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [otpBusy, setOtpBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [editMobile, setEditMobile] = useState(false);
  const [newMobile, setNewMobile] = useState("");
  const [mobileBusy, setMobileBusy] = useState(false);
  const [priv, setPriv] = useState({ show_email: true, show_phone: true, show_photo: true });
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const err = (e) => toast.show(e instanceof Error ? e.message : "Failed", "error");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, me] = await Promise.all([api("/auth/sessions"), api("/auth/audit"), api("/auth/me")]);
      setSessions(s); setAudit(a);
      if (me.privacy) setPriv({ show_email: !!me.privacy.show_email, show_phone: !!me.privacy.show_phone, show_photo: !!me.privacy.show_photo });
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resendEmail = async () => { setEmailBusy(true); try { await api("/auth/resend-email-verification", { method: "POST" }); toast.show("Verification email sent", "success"); } catch (e) { err(e); } finally { setEmailBusy(false); } };
  const sendOtp = async () => { setOtpBusy(true); try { await api("/auth/send-mobile-otp", { method: "POST", body: {} }); toast.show("Code sent by SMS", "success"); setShowOtp(true); } catch (e) { err(e); } finally { setOtpBusy(false); } };
  const verifyOtp = async () => {
    if (!otp.trim()) return;
    setOtpBusy(true);
    try { await api("/auth/verify-mobile-otp", { method: "POST", body: { code: otp.trim() } }); toast.show("Mobile verified!", "success"); setOtp(""); setShowOtp(false); await refreshMe(); } catch (e) { err(e); } finally { setOtpBusy(false); }
  };
  const saveMobile = async () => {
    if (!newMobile) return toast.show("Enter a valid mobile number", "error");
    setMobileBusy(true);
    try { await api("/auth/change-mobile", { method: "POST", body: { mobile_number: newMobile } }); toast.show("OTP sent to your new mobile", "success"); setEditMobile(false); setShowOtp(true); setNewMobile(""); await refreshMe(); } catch (e) { err(e); } finally { setMobileBusy(false); }
  };
  const revoke = async (sid) => { try { await api(`/auth/sessions/${sid}/revoke`, { method: "POST" }); toast.show("Device signed out", "info"); load(); } catch (e) { err(e); } };
  const setPrivacy = async (key, value) => {
    setPriv((p) => ({ ...p, [key]: value }));
    try { await api("/auth/privacy", { method: "PATCH", body: { [key]: value } }); } catch (e) { err(e); setPriv((p) => ({ ...p, [key]: !value })); }
  };
  const changePw = async () => {
    if (!cur || next.length < 6) return toast.show("Enter current password and 6+ char new password", "error");
    setPwBusy(true);
    try { await api("/auth/change-password", { method: "POST", body: { current_password: cur, new_password: next } }); toast.show("Password changed. Other devices signed out.", "success"); setCur(""); setNext(""); load(); } catch (e) { err(e); } finally { setPwBusy(false); }
  };
  const StatusIcon = ({ ok }) => (ok ? <CheckCircle2 size={20} className="shrink-0 text-a-success" /> : <AlertCircle size={20} className="shrink-0 text-a-warning" />);

  return (
    <StackPage title="Security & privacy" testID="security-page">
      <Card>
        <Overline className="mb-2">Verify contact</Overline>
        <div className="flex items-center gap-3 py-2">
          <StatusIcon ok={user?.email_verified} />
          <div className="min-w-0 flex-1"><p className="font-bold text-a-text">Email</p><p className="truncate text-xs text-a-muted">{user?.email} · {user?.email_verified ? "Verified" : "Not verified"}</p></div>
          {!user?.email_verified ? <Button testID="security-send-email" label={emailBusy ? "…" : "Send link"} fullWidth={false} onPress={resendEmail} variant="secondary" size="sm" /> : null}
        </div>
        <div className="flex items-center gap-3 py-2">
          <StatusIcon ok={user?.mobile_verified} />
          <div className="min-w-0 flex-1"><p className="font-bold text-a-text">Mobile</p><p className="truncate text-xs text-a-muted">{user?.mobile_number ? maskE164(user.mobile_number) : "not set"} · {user?.mobile_verified ? "Verified" : "Not verified"}</p></div>
          {!user?.mobile_verified && user?.mobile_number && !editMobile ? <Button testID="security-send-otp" label={otpBusy ? "…" : "Send OTP"} fullWidth={false} onPress={sendOtp} variant="secondary" size="sm" /> : null}
          <button onClick={() => setEditMobile((v) => !v)} className="text-[13px] font-bold text-a-primary" data-testid="security-mobile-toggle">{editMobile ? "Cancel" : user?.mobile_number ? "Change" : "Add"}</button>
        </div>
        {editMobile ? (
          <div className="mt-2 space-y-2.5">
            <PhoneInput testID="security-new-mobile" label="Mobile number" placeholder="98765 43210" valueE164={newMobile} onChangeE164={(e) => setNewMobile(e)} />
            <Button testID="security-save-mobile" label="Save & send OTP" onPress={saveMobile} loading={mobileBusy} />
          </div>
        ) : null}
        {showOtp && !editMobile ? (
          <div className="mt-2 flex gap-2">
            <Input testID="security-otp" placeholder="6-digit code" inputMode="numeric" value={otp} onChangeText={setOtp} onSubmitEditing={verifyOtp} />
            <Button testID="security-verify-otp" label="Verify" onPress={verifyOtp} loading={otpBusy} fullWidth={false} />
          </div>
        ) : null}
      </Card>
      <Card>
        <Overline className="mb-2">Change password</Overline>
        <div className="space-y-2.5">
          <Input testID="security-current-password" label="Current password" type="password" value={cur} onChangeText={setCur} />
          <Input testID="security-new-password" label="New password (6+ chars)" type="password" value={next} onChangeText={setNext} />
          <Button testID="security-change-password" label="Change password" onPress={changePw} loading={pwBusy} />
        </div>
      </Card>
      <Card>
        <Overline>Privacy</Overline>
        <p className="mb-1 mt-1 text-xs text-a-muted">Control what other workspace members can see about you.</p>
        {["show_email", "show_phone", "show_photo"].map((k) => (
          <div key={k} className="flex items-center gap-3 py-2.5">
            <p className="flex-1 font-bold text-a-text">{k === "show_email" ? "Show my email" : k === "show_phone" ? "Show my phone" : "Show my photo"}</p>
            <Toggle testID={`security-${k}`} value={priv[k]} onChange={(v) => setPrivacy(k, v)} />
          </div>
        ))}
      </Card>
      <Card>
        <Overline className="mb-2">Signed-in devices</Overline>
        {loading ? <div className="flex justify-center py-3"><Spinner /></div> : sessions.length === 0 ? <p className="text-a-muted">No active sessions.</p> : sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-3 py-2.5" data-testid={`session-${s.id}`}>
            {s.device_platform === "web" ? <Monitor size={20} className="shrink-0 text-a-primary" /> : <Smartphone size={20} className="shrink-0 text-a-primary" />}
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-a-text">{s.device_name || "Unknown"}{s.current ? " (this device)" : ""}</p>
              <p className="truncate text-xs text-a-muted">{s.device_platform} · {s.ip || "-"} · last active {new Date(s.last_seen_at).toLocaleString()}</p>
            </div>
            {!s.current ? <button onClick={() => revoke(s.id)} className="text-a-danger" title="Sign out device"><LogOut size={20} /></button> : null}
          </div>
        ))}
      </Card>
      <Card>
        <Overline className="mb-2">Recent security activity</Overline>
        {audit.length === 0 ? <p className="text-a-muted">No activity yet.</p> : audit.slice(0, 20).map((a) => (
          <div key={a.id} className="border-b border-a-border py-2 last:border-0">
            <p className="font-semibold capitalize text-a-text">{a.action.replace(/_/g, " ")}</p>
            <p className="text-xs text-a-muted">{new Date(a.created_at).toLocaleString()}{a.ip ? ` · ${a.ip}` : ""}</p>
          </div>
        ))}
      </Card>
    </StackPage>
  );
}
